// Parses controller instructions – full ICAO phraseology (typed or recognised speech) as well as short forms
// for the command line – into structured commands. Context (active callsigns, fixes, taxiways, units) resolves
// ambiguities: "Lufthansa four tango kilo", "DLH4TK", "4TK" and "Delta Bravo Charlie" all address an aircraft.
import type { Cmd, TaxiTarget } from '../sim/commands';
import { WORD_DIGIT, WORD_LETTER } from './spoken';

export interface ParseCtx {
  aircraft: { cs: string; telephony: string; flightId: string }[];
  fixes: string[];
  /** Spoken names of fixes (lowercase) → fix code. */
  fixSay: Record<string, string>;
  taxiways: string[];
  runways: string[];
  stands: string[];
  /** unit id → callsign, e.g. TWR → 'Bremen Tower'. */
  units: Record<string, string>;
  /** Defaults for short forms. */
  defaults: { depRwy: string; arrRwy: string };
  /** Selected aircraft (used when no callsign is given). */
  selected?: string | null;
  /** Default clearance for the "C"/"CLR" short form. */
  clearanceFor?: (cs: string) => Extract<Cmd, { k: 'CLEARANCE' }> | null;
  /** Default departure holding point for "T"/"TAXI" short form. */
  holdFor?: (cs: string) => { rwy: string; hold: string } | null;
  /** Stand of an arrival for "T" short form. */
  standFor?: (cs: string) => string | null;
}

export interface ParseResult { cs: string | null; cmds: Cmd[]; unknown: string[]; error?: string }

const UNIT_WORDS: Record<string, string[]> = {
  DEL: ['delivery', 'clearance'], APN: ['apron', 'vorfeld'], GND: ['ground', 'rollkontrolle'], TWR: ['tower', 'turm'],
  DEP: ['departure'], APP: ['approach', 'radar', 'director', 'arrival'], CTR: ['center', 'centre', 'control'],
};

/** Tokenises and converts spoken numbers/letters: "four tango kilo" → ["4", "t", "k"]. */
export function normalise(text: string): string[] {
  const raw = text.toLowerCase().replace(/[,;.!?]/g, ' ').replace(/[-/](?=\D)/g, ' ').replace(/\s+/g, ' ').trim();
  const out: string[] = [];
  for (const w of raw.split(' ')) {
    if (!w) continue;
    if (WORD_DIGIT[w] !== undefined && !['to', 'too', 'for'].includes(w)) out.push('#' + WORD_DIGIT[w]!);
    else if (WORD_LETTER[w] !== undefined && w.length > 1) out.push(WORD_LETTER[w]!.toLowerCase());
    else if ((w === 'decimal' || w === 'point') && out.length && /^#?\d+$/.test(out[out.length - 1]!)) out.push('.');
    else if (w === 'thousand' || w === 'hundred') out.push(w);
    else out.push(w);
  }
  // join spoken digits ("#1 #2 #1 . #7 #5 #5" → "121.755", "#2 #7 #0" → "270"); typed numbers stay as they are
  const joined: string[] = [];
  for (const t of out) {
    const prev = joined[joined.length - 1];
    const spoken = t.startsWith('#') || t === '.';
    if (prev !== undefined && spoken && /^#[\d.]+$/.test(prev)) joined[joined.length - 1] = prev + t.replace('#', '');
    else joined.push(t);
  }
  // "four thousand five hundred" → 4500
  const res: string[] = [];
  for (let k = 0; k < joined.length; k++) {
    const t = joined[k]!.replace('#', '');
    if ((joined[k + 1] === 'thousand' || joined[k + 1] === 'hundred') && /^\d+$/.test(t)) {
      let v = Number(t) * (joined[k + 1] === 'thousand' ? 1000 : 100);
      k++;
      const n2 = joined[k + 1]?.replace('#', '');
      if (joined[k] === 'thousand' && n2 && /^\d$/.test(n2) && joined[k + 2] === 'hundred') { v += Number(n2) * 100; k += 2; }
      res.push(String(v));
    } else res.push(t);
  }
  return res;
}

function resolveCallsign(tokens: string[], ctx: ParseCtx): { cs: string; used: number } | null {
  const acs = ctx.aircraft;
  const t0 = tokens[0] ?? '';
  // direct ICAO callsign: "dlh4tk"
  for (const a of acs) if (a.cs.toLowerCase() === t0) return { cs: a.cs, used: 1 };
  // telephony + flight id possibly split into letters/digits: "lufthansa 4 t k" / "lufthansa 4tk"
  for (const a of acs) {
    if (!a.telephony) continue;
    const tel = a.telephony.toLowerCase().split(' ');
    if (tokens.slice(0, tel.length).join(' ') !== tel.join(' ')) continue;
    const id = a.flightId.toLowerCase();
    let acc = '', n = tel.length;
    while (n < tokens.length && acc.length < id.length && /^[a-z0-9]+$/.test(tokens[n]!)) { acc += tokens[n]; n++; if (acc === id) return { cs: a.cs, used: n }; }
  }
  // registrations: "d e a b c" / "deabc" / abbreviated "d b c"
  const letters: string[] = [];
  for (let i = 0; i < Math.min(5, tokens.length); i++) { if (/^[a-z]$/.test(tokens[i]!) || /^[a-z]{2,5}$/.test(tokens[i]!)) letters.push(tokens[i]!); else break; }
  const reg = letters.join('');
  for (const a of acs) {
    if (a.telephony) continue;
    const r = a.cs.toLowerCase();
    for (let k = letters.length; k >= 1; k--) {
      const s = letters.slice(0, k).join('');
      if (s === r || (s.length === 3 && s === r[0]! + r.slice(-2))) return { cs: a.cs, used: k };
    }
  }
  void reg;
  // flight id alone if unique: "4tk"
  const byId = acs.filter(a => a.flightId.toLowerCase() === t0 || a.cs.toLowerCase().endsWith(t0) && t0.length >= 3);
  if (byId.length === 1) return { cs: byId[0]!.cs, used: 1 };
  return null;
}

const num = (s?: string): number | null => (s !== undefined && /^\d+(\.\d+)?$/.test(s) ? Number(s) : null);
const rwyTok = (s: string | undefined, ctx: ParseCtx): string | null => {
  if (!s) return null;
  const m = /^(\d{1,2})([lcr]?)$/.exec(s);
  if (!m) return null;
  const id = m[1]!.padStart(2, '0') + m[2]!.toUpperCase();
  return ctx.runways.includes(id) ? id : null;
};

export function parseInstruction(text: string, ctx: ParseCtx): ParseResult {
  let tokens = normalise(text);
  const res: ParseResult = { cs: null, cmds: [], unknown: [] };
  const cs = resolveCallsign(tokens, ctx);
  if (cs) { res.cs = cs.cs; tokens = tokens.slice(cs.used); }
  else res.cs = ctx.selected ?? null;
  // drop station name ("bremen tower") after the callsign
  while (tokens.length && (Object.values(ctx.units).some(u => u.toLowerCase().split(' ').includes(tokens[0]!)) && !['tower', 'ground', 'radar', 'apron', 'delivery'].includes(tokens[0]!) || tokens[0] === 'bremen' || tokens[0] === 'hamburg' || tokens[0] === 'munich' || tokens[0] === 'frankfurt' || tokens[0] === 'langen' || tokens[0] === 'munchen')) {
    if (['tower', 'ground', 'radar', 'apron', 'delivery'].includes(tokens[1] ?? '')) tokens = tokens.slice(2); else tokens = tokens.slice(1);
  }
  const csId = res.cs;
  let i = 0;
  const peek = (k = 0) => tokens[i + k];
  const isWord = (...ws: string[]) => ws.includes(peek() ?? '');
  const skip = (...ws: string[]) => { while (i < tokens.length && ws.includes(tokens[i]!)) i++; };
  const fixOf = (t?: string): string | null => {
    if (!t) return null;
    const up = t.toUpperCase();
    if (ctx.fixes.includes(up)) return up;
    const s = ctx.fixSay[t];
    return s ?? null;
  };
  const taxiways = new Set(ctx.taxiways.map(t => t.toLowerCase()));
  const readVia = (): string[] => {
    const via: string[] = [];
    while (i < tokens.length) {
      const t = tokens[i]!;
      if (t === 'and' && taxiways.has(tokens[i + 1] ?? '')) { i++; continue; }
      if (!taxiways.has(t)) break;
      via.push(t.toUpperCase());
      i++;
    }
    return via;
  };

  while (i < tokens.length) {
    const t = tokens[i]!;
    const start = i;
    // ---------------- short forms used on the command line
    if ((t === 'c' || t === 'clr') && tokens.length === 1 && csId && ctx.clearanceFor) {
      const c = ctx.clearanceFor(csId); if (c) res.cmds.push(c); i++; continue;
    }
    if (t === 'rb' || (t === 'readback' && peek(1) === 'correct')) { res.cmds.push({ k: 'RB_OK' }); i += t === 'rb' ? 1 : 2; continue; }
    if (t === 'pb' || t === 'push' || t === 'pushback') {
      i++; skip('back', 'and', 'start', 'up', 'startup', 'approved');
      let facing: string | undefined;
      if (isWord('facing')) { i++; if (['north', 'east', 'south', 'west'].includes(peek() ?? '')) { facing = peek(); i++; } }
      res.cmds.push({ k: 'PUSH', facing }); continue;
    }
    if (t === 'su' || ((t === 'start' || t === 'startup') && (peek(1) === 'up' || peek(1) === 'approved'))) { i++; skip('up', 'approved'); res.cmds.push({ k: 'STARTUP' }); continue; }
    if (t === 'cleared' && peek(1) === 'to' && peek(2) && !['land'].includes(peek(2)!)) {
      // IFR clearance: cleared to <dest> via <sid> departure, climb initially <alt>, squawk <code>
      i += 2;
      const dest = (tokens[i] ?? '').toUpperCase(); i++;
      let sid = '';
      if (isWord('via')) {
        i++;
        const a = tokens[i] ?? '', b = tokens[i + 1] ?? '';
        const fx = fixOf(a);
        // "nasuk 1 z" / "nasuk1z"
        const m = /^([a-z]{2,5})(\d)([a-z])$/.exec(a);
        if (m) { sid = `${(fixOf(m[1]) ?? m[1]!.toUpperCase())} ${m[2]}${m[3]!.toUpperCase()}`; i++; }
        else if (fx && /^\d[a-z]$/.test(b)) { sid = `${fx} ${b.toUpperCase()}`; i += 2; }
        else if (fx && /^\d$/.test(b) && /^[a-z]$/.test(tokens[i + 2] ?? '')) { sid = `${fx} ${b}${tokens[i + 2]!.toUpperCase()}`; i += 3; }
        skip('departure');
      }
      let altFt = 0, squawk = '';
      while (i < tokens.length && !(tokens[i] === 'cleared')) {
        if (isWord('climb')) { i++; skip('initially', 'to', 'altitude'); const n = num(peek()); if (n !== null) { altFt = n < 100 ? n * 1000 : n; i++; } skip('feet', 'ft'); continue; }
        if (isWord('squawk')) { i++; const n = peek(); if (n && /^[0-7]{4}$/.test(n)) { squawk = n; i++; } continue; }
        if (isWord('flight', 'planned', 'route', 'and')) { i++; continue; }
        break;
      }
      const def = csId ? ctx.clearanceFor?.(csId) : null;
      const destIcao = def && (dest.length !== 4 || dest === def.dest) ? def.dest : dest;
      res.cmds.push({ k: 'CLEARANCE', dest: def?.dest && dest.length !== 4 ? def.dest : destIcao, sid: sid || def?.sid || '', altFt: altFt || def?.altFt || 0, squawk: squawk || def?.squawk || '' });
      continue;
    }
    if (t === 'taxi' || t === 't' || t === 'tx') {
      i++; skip('to');
      let target: TaxiTarget | null = null;
      if (isWord('holding', 'hp', 'hold')) {
        i++; skip('point', 'position');
        const hold = (tokens[i] ?? '').toUpperCase(); i++;
        let rwy = ctx.defaults.depRwy;
        if (isWord('runway', 'rwy')) { i++; rwy = rwyTok(peek(), ctx) ?? rwy; i++; }
        target = { kind: 'HOLD', rwy, hold };
      } else if (isWord('stand', 'position', 'parking', 'gate')) {
        i++;
        const st = (tokens[i] ?? '').toUpperCase(); i++;
        const id = ctx.stands.find(s => s === st || s === st.padStart(2, '0') || s.replace(/^0/, '') === st) ?? st;
        target = { kind: 'STAND', stand: id };
      } else if (csId && taxiways.has(peek() ?? '') && ctx.holdFor) {
        // short form "T F" – holding point F of the departure runway
        const hold = (tokens[i] ?? '').toUpperCase();
        const h = ctx.holdFor(csId);
        if (h && ctx.taxiways.includes(hold) && (peek(1) === 'via' || i + 1 >= tokens.length)) { target = { kind: 'HOLD', rwy: h.rwy, hold }; i++; }
      }
      if (!target && csId) {
        const h = ctx.holdFor?.(csId), st = ctx.standFor?.(csId);
        if (st) target = { kind: 'STAND', stand: st };
        else if (h) target = { kind: 'HOLD', rwy: h.rwy, hold: h.hold };
      }
      let via: string[] = [];
      if (isWord('via')) { i++; via = readVia(); }
      let holdShort: string | undefined;
      skip('and');
      if (isWord('hold') && peek(1) === 'short') { i += 2; skip('of'); skip('runway', 'rwy'); holdShort = rwyTok(peek(), ctx) ?? undefined; if (holdShort) i++; }
      if (target) res.cmds.push({ k: 'TAXI', target, via, holdShort });
      else res.unknown.push(tokens.slice(start, i).join(' '));
      continue;
    }
    if ((t === 'hold' && peek(1) === 'position') || t === 'hpos' || t === 'stop') { i += t === 'hold' ? 2 : 1; res.cmds.push({ k: 'HOLD_POS' }); continue; }
    if (t === 'continue' && (peek(1) === 'taxi' || peek(1) === 'taxiing')) { i += 2; res.cmds.push({ k: 'CONT_TAXI' }); continue; }
    if (t === 'continue' && peek(1) === 'approach') { i += 2; res.cmds.push({ k: 'CONTINUE' }); continue; }
    if (t === 'cross' || t === 'x') { i++; skip('runway', 'rwy'); const r = rwyTok(peek(), ctx) ?? ctx.defaults.depRwy; if (rwyTok(peek(), ctx)) i++; res.cmds.push({ k: 'CROSS', rwy: r }); continue; }
    if (t === 'hold' && peek(1) === 'short') { i += 2; skip('of', 'runway', 'rwy'); const r = rwyTok(peek(), ctx); if (r) { i++; res.cmds.push({ k: 'HOLD_SHORT', rwy: r }); } continue; }
    if (t === 'lu' || (t === 'line' && peek(1) === 'up')) {
      i += t === 'lu' ? 1 : 2; skip('and', 'wait');
      let rwy = ctx.defaults.depRwy, hold: string | undefined;
      if (isWord('runway', 'rwy')) { i++; rwy = rwyTok(peek(), ctx) ?? rwy; i++; }
      else if (rwyTok(peek(), ctx)) { rwy = rwyTok(peek(), ctx)!; i++; }
      skip('and', 'wait');
      if (isWord('intersection', 'via')) { i++; hold = (tokens[i] ?? '').toUpperCase(); i++; }
      res.cmds.push({ k: 'LINEUP', rwy, hold }); continue;
    }
    if (t === 'cto' || (t === 'cleared' && peek(1) === 'for' && (peek(2) === 'takeoff' || peek(2) === 'take'))) {
      i += t === 'cto' ? 1 : peek(2) === 'take' ? 4 : 3;
      let rwy = ctx.defaults.depRwy;
      if (rwyTok(peek(), ctx)) { rwy = rwyTok(peek(), ctx)!; i++; }
      res.cmds.push({ k: 'TAKEOFF', rwy }); continue;
    }
    if (t === 'runway' && rwyTok(peek(1), ctx)) {
      // "runway 26 cleared for take-off / cleared to land"
      const r = rwyTok(peek(1), ctx)!;
      if (peek(2) === 'cleared' && peek(3) === 'to' && peek(4) === 'land') { i += 5; res.cmds.push({ k: 'LAND', rwy: r }); continue; }
      if (peek(2) === 'cleared' && peek(3) === 'for') { i += peek(4) === 'take' ? 6 : 5; res.cmds.push({ k: 'TAKEOFF', rwy: r }); continue; }
      i += 2; continue;
    }
    if (t === 'cancel' && (peek(1) === 'takeoff' || peek(1) === 'take')) { i += peek(1) === 'take' ? 3 : 2; res.cmds.push({ k: 'CANCEL_TAKEOFF' }); continue; }
    if (t === 'ctl' || (t === 'cleared' && peek(1) === 'to' && peek(2) === 'land')) {
      i += t === 'ctl' ? 1 : 3;
      let rwy = ctx.defaults.arrRwy;
      if (isWord('runway')) i++;
      if (rwyTok(peek(), ctx)) { rwy = rwyTok(peek(), ctx)!; i++; }
      res.cmds.push({ k: 'LAND', rwy }); continue;
    }
    if (t === 'ga' || (t === 'go' && peek(1) === 'around')) { i += t === 'ga' ? 1 : 2; res.cmds.push({ k: 'GO_AROUND' }); continue; }
    if (t === 'vacate' || t === 'vac') { i++; skip('via', 'left', 'right'); const v = (tokens[i] ?? '').toUpperCase(); if (v) { i++; res.cmds.push({ k: 'VACATE', via: v }); } continue; }
    if (t === 'wind') { i++; while (i < tokens.length && /^(\d+|degrees|knots|gusting|calm|variable)$/.test(tokens[i]!)) i++; continue; }
    // ---------------- radar
    const hm = /^([hlr])(\d{1,3})$/.exec(t);
    if (hm) { i++; res.cmds.push({ k: 'HDG', hdg: Number(hm[2]) || 360, turn: hm[1] === 'l' ? 'L' : hm[1] === 'r' ? 'R' : undefined }); continue; }
    if (t === 'turn' || t === 'fly' || t === 'heading' || t === 'hdg') {
      i++;
      let turn: 'L' | 'R' | undefined;
      if (isWord('left')) { turn = 'L'; i++; } else if (isWord('right')) { turn = 'R'; i++; }
      skip('heading', 'hdg');
      const n = num(peek());
      if (n !== null) { i++; res.cmds.push({ k: 'HDG', hdg: n % 360 || 360, turn }); }
      else if (isWord('present') || isWord('continue')) { i++; skip('heading'); }
      continue;
    }
    const am = /^(a|fl|f)(\d{2,5})$/.exec(t);
    if (am) { i++; const n = Number(am[2]); res.cmds.push(am[1] === 'a' ? { k: 'ALT', ft: n < 100 ? n * 100 : n, fl: false } : { k: 'ALT', ft: n * 100, fl: true }); continue; }
    if (t === 'climb' || t === 'descend' || t === 'maintain' || t === 'alt' || t === 'fl') {
      i++; skip('to', 'and', 'maintain');
      if (isWord('flight') && peek(1) === 'level') { i += 2; const n = num(peek()); if (n !== null) { i++; res.cmds.push({ k: 'ALT', ft: n * 100, fl: true }); } continue; }
      if (t === 'fl') { const n = num(peek()); if (n !== null) { i++; res.cmds.push({ k: 'ALT', ft: n * 100, fl: true }); } continue; }
      skip('altitude');
      const n = num(peek());
      if (n !== null) { i++; skip('feet', 'ft'); res.cmds.push({ k: 'ALT', ft: n < 100 ? n * 100 : n, fl: false }); }
      skip('qnh'); if (num(peek()) !== null && (num(peek()) ?? 0) > 900) i++;
      continue;
    }
    const sm = /^s(\d{2,3})$/.exec(t);
    if (sm) { i++; res.cmds.push({ k: 'SPD', kt: Number(sm[1]) }); continue; }
    if (t === 'reduce' || t === 'increase' || t === 'speed' || t === 'spd') {
      i++; skip('speed', 'to');
      const n = num(peek());
      if (n !== null) { i++; skip('knots', 'kt'); res.cmds.push({ k: 'SPD', kt: n }); }
      continue;
    }
    if (t === 'no' && peek(1) === 'speed') { i += 2; skip('restriction', 'restrictions'); res.cmds.push({ k: 'SPD_OWN' }); continue; }
    if (t === 'resume' && (peek(1) === 'normal' || peek(1) === 'own')) { i += 2; skip('speed', 'navigation'); res.cmds.push(peek(-1) === 'speed' || tokens[i - 1] === 'speed' ? { k: 'SPD_OWN' } : { k: 'RESUME_OWN' }); continue; }
    if (t === 'proceed' || t === 'direct' || t === 'dct' || t === 'd') {
      i++; skip('direct', 'to');
      const f = fixOf(peek());
      if (f) { i++; res.cmds.push({ k: 'DIRECT', fix: f }); } else res.unknown.push(tokens.slice(start, i + 1).join(' '));
      continue;
    }
    if (t === 'ils' || (t === 'cleared' && (peek(1) === 'ils' || (peek(1) === 'i' && peek(2) === 'l')))) {
      i += t === 'ils' ? 1 : peek(1) === 'ils' ? 2 : 4;
      skip('approach', 'runway', 'rwy');
      let rwy = ctx.defaults.arrRwy;
      if (rwyTok(peek(), ctx)) { rwy = rwyTok(peek(), ctx)!; i++; }
      res.cmds.push({ k: 'ILS', rwy }); continue;
    }
    if (t === 'hold' && (peek(1) === 'at' || fixOf(peek(1)))) {
      i++; skip('at', 'over');
      const f = fixOf(peek());
      if (f) { i++; skip('as', 'published'); res.cmds.push({ k: 'HOLD_AT', fix: f }); }
      continue;
    }
    if (t === 'contact' || t === 'ct' || t === 'monitor') {
      i++;
      let unit: string | null = null;
      for (let k = 0; k < 3 && i + k < tokens.length && !unit; k++) {
        const w = tokens[i + k]!;
        for (const [u, words] of Object.entries(UNIT_WORDS)) if (words.includes(w) && ctx.units[u]) { unit = u; i += k + 1; break; }
        if (!unit && /^\d{3}\.\d{1,3}$/.test(w)) {
          // contact by frequency only
          void w;
        }
      }
      while (i < tokens.length && (/^\d{3}\.\d{1,3}$/.test(tokens[i]!) || ['bremen', 'hamburg', 'munich', 'frankfurt', 'langen', 'on', 'goodbye', 'bye', 'tschuss', 'servus'].includes(tokens[i]!))) i++;
      if (unit) res.cmds.push({ k: 'CONTACT', unit });
      else res.unknown.push(tokens.slice(start, i + 1).join(' '));
      continue;
    }
    if (t === 'squawk' || t === 'sq') { i++; const n = peek(); if (n && /^[0-7]{4}$/.test(n)) { i++; res.cmds.push({ k: 'SQUAWK', code: n }); } continue; }
    if (t === 'identified' || (t === 'radar' && peek(1) === 'contact') || t === 'id') { i += t === 'radar' ? 2 : 1; res.cmds.push({ k: 'IDENTIFIED' }); continue; }
    if (t === 'say' && peek(1) === 'again') { i += 2; res.cmds.push({ k: 'SAY_AGAIN' }); continue; }
    if (t === 'report' && peek(1) === 'established') { i += 2; res.cmds.push({ k: 'REPORT_EST' }); continue; }
    if (['and', 'then', 'please', 'now', 'immediately', 'expedite', 'negative', 'correction', 'i', 'say', 'again'].includes(t)) { i++; continue; }
    res.unknown.push(t);
    i++;
  }
  if (!res.cs && res.cmds.length) res.error = 'Rufzeichen nicht erkannt';
  return res;
}
